import {useEffect, useRef} from 'react';

import {
  type AutoPanelCountProperty,
  type PanelCountName,
  type PanelCountProperty,
  type PanelTypeCounts,
  trackWorkspacePanelInventory,
} from '../../../services/analytics/workspacePanelInventoryEvents';
import {type NamedWorkspaceType} from '../../../services/analytics/workspacesEvents';
import {useReduxStore} from '../../../state/hooks';
import {useActiveHistoryKeyInfo} from '../../../state/panelGenHistoryKeys/useActiveHistoryKeyInfo';
import type * as PanelTypes from '../../../state/views/panel/types';
import {type LoadableView} from '../../../state/views/types';
import {
  getNamedWorkspaceIdForView,
  viewIsLegacyWorkspace,
} from '../../../state/workspaces/names';
import {type RunHistoryKeyInfoKeys} from '../../../types/run';
import {type PlotType} from '../../../util/plotHelpers/types';
import {type PlotStyle} from '../../PanelBarChart/types';
import {mediaCardTypeForMetric} from '../../PanelMediaBrowser/utils/mediaCardTypeForMetric';
import {useWorkspaceRefsContext} from '../../Workspace/WorkspaceRefsContext';
import {useShouldAutoGeneratePanels} from '../../WorkspaceDrawer/Settings/hooks';
import {panelViews, type SectionWithRef} from '../types';
import {
  getInventoryWorkspaceKey,
  recordInventorySent,
  wasInventorySentRecently,
} from './panelInventoryStorage';
import {scheduleInventoryCount} from './scheduleInventoryCount';

export const PANEL_INVENTORY_SCHEMA_VERSION = 1;

/** The config field that picks a panel's visualization, unformatted. */
function rawSubType(panel: PanelTypes.Panel): unknown {
  switch (panel.viewType) {
    case panelViews.runHistoryLinePlot:
      return panel.config?.plotType;
    case panelViews.barChart:
      return panel.config?.plotStyle;
    case panelViews.vega2:
      return panel.config?.panelDefId;
    case panelViews.mediaBrowser:
      return panel.config?.mediaKeys;
    default:
      return undefined;
  }
}

function formatSubType(
  viewType: string,
  raw: unknown,
  keys: RunHistoryKeyInfoKeys
): string | null {
  switch (viewType) {
    case panelViews.runHistoryLinePlot:
      return (raw as PlotType | undefined) ?? ('line' satisfies PlotType);
    case panelViews.barChart:
      return (raw as PlotStyle | undefined) ?? ('bar' satisfies PlotStyle);
    case panelViews.vega2: {
      // Users custom vega charts show as <entity>/<name> or lib:<id>
      // reporting those as custom, for templates strips the wandb/ prefix
      const panelDefId = (raw as string | undefined) || 'wandb/line/v0';
      return BUILT_IN_PRESET.exec(panelDefId)?.[1] ?? 'custom';
    }
    case panelViews.mediaBrowser: {
      // The config only names the key; its logged types say what it shows,
      // by the rule panel generation and the media pickers use.
      const typeCounts = typeof raw === 'string' ? keys[raw]?.typeCounts : [];
      return (
        mediaCardTypeForMetric({
          types: (typeCounts ?? []).map(({type}) => type),
        }) ?? 'unknown'
      );
    }
    default:
      return null;
  }
}

const BUILT_IN_PRESET = /^wandb\/([^/]+\/v\d+)$/;

/**
 * Segment's warehouse connector truncates string columns, including
 * stringified arrays, at 512 characters, which would cut a kind list mid-JSON.
 */
export const MAX_KIND_LIST_JSON_LENGTH = 512;

/** The longest prefix of `kinds` whose JSON fits, so a trimmed list is valid. */
export function fitKindList(kinds: string[]) {
  let length = 2; // []
  for (let i = 0; i < kinds.length; i++) {
    length += JSON.stringify(kinds[i]).length + (i > 0 ? 1 : 0);
    if (length > MAX_KIND_LIST_JSON_LENGTH) {
      return {kinds: kinds.slice(0, i), truncated: true};
    }
  }
  return {kinds, truncated: false};
}

const countProperty = (name: PanelCountName, isAuto: boolean) =>
  `num${isAuto ? 'Auto' : ''}${name.charAt(0).toUpperCase()}${name.slice(1)}Panels` as
    | PanelCountProperty
    | AutoPanelCountProperty;

const countTarget = (name: PanelCountName) => ({
  manual: countProperty(name, false),
  auto: countProperty(name, true),
  kind: name,
});

/**
 * viewType -> the count properties and kind name it adds to, e.g. 'Bar Chart'
 * -> numBarChartPanels / numAutoBarChartPanels / barChart. Built once so the
 * count doesn't rebuild property names for every panel. A viewType missing
 * from here (not in panelViews) adds only to numPanels and numAutoPanels.
 */
const COUNT_TARGET_BY_VIEW_TYPE = new Map(
  (Object.keys(panelViews) as PanelCountName[]).map(name => [
    panelViews[name] as string,
    countTarget(name),
  ])
);

type Tally = {count: number; rawSubTypes: Set<unknown>};

/** Panels, or raw sub-types such as media keys, between chances to pause. */
const STEPS_PER_YIELD = 1000;

/**
 * Counts the panel bank, yielding every `STEPS_PER_YIELD` steps so
 * we can pause if the slice time has elapsed, allow it to be interrupted
 * and then continue.
 */
function* countInventory(
  sections: readonly SectionWithRef[],
  keys: RunHistoryKeyInfoKeys
) {
  let steps = 0;
  const shouldYield = () => ++steps % STEPS_PER_YIELD === 0;
  let numAutoSections = 0;

  const tallies = {
    manual: new Map<string, Tally>(),
    auto: new Map<string, Tally>(),
  };
  for (const section of sections) {
    // Same rule as panels: a section counts as manual only when its flag is
    // explicitly false, which creating a section by hand writes.
    numAutoSections += section.isPanelsAuto !== false ? 1 : 0;
    for (const panel of section.panels) {
      const byViewType = panel.isAuto !== false ? tallies.auto : tallies.manual;
      let tally = byViewType.get(panel.viewType);
      if (tally == null) {
        tally = {count: 0, rawSubTypes: new Set()};
        byViewType.set(panel.viewType, tally);
      }
      tally.count++;
      const raw = rawSubType(panel);
      if (Array.isArray(raw) && raw.length > 0) {
        for (const key of raw) {
          tally.rawSubTypes.add(key);
        }
      } else {
        tally.rawSubTypes.add(Array.isArray(raw) ? undefined : raw);
      }
      if (shouldYield()) {
        yield;
      }
    }
  }

  const typeCounts = Object.fromEntries(
    [...COUNT_TARGET_BY_VIEW_TYPE.values()].flatMap(t => [
      [t.manual, 0],
      [t.auto, 0],
    ])
  ) as PanelTypeCounts;
  let numPanels = 0;
  let numAutoPanels = 0;
  const kinds = {manual: new Set<string>(), auto: new Set<string>()};
  for (const isAuto of [false, true]) {
    const byViewType = isAuto ? tallies.auto : tallies.manual;
    for (const [viewType, {count, rawSubTypes}] of byViewType) {
      numPanels += count;
      numAutoPanels += isAuto ? count : 0;
      const target = COUNT_TARGET_BY_VIEW_TYPE.get(viewType);
      if (target == null) {
        continue;
      }
      typeCounts[isAuto ? target.auto : target.manual] += count;
      // Many raw values (a workspace's media keys) share one sub-type, so
      // de-duplicate before building each kind string.
      const subTypes = new Set<string | null>();
      for (const raw of rawSubTypes) {
        subTypes.add(formatSubType(viewType, raw, keys));
        if (shouldYield()) {
          yield;
        }
      }
      for (const subType of subTypes) {
        (isAuto ? kinds.auto : kinds.manual).add(
          subType == null ? target.kind : `${target.kind}:${subType}`
        );
      }
    }
  }

  const panelKinds = fitKindList([...kinds.manual].sort());
  const autoPanelKinds = fitKindList([...kinds.auto].sort());
  return {
    numSections: sections.length,
    numAutoSections,
    numPanels,
    numAutoPanels,
    ...typeCounts,
    panelKinds: panelKinds.kinds,
    autoPanelKinds: autoPanelKinds.kinds,
    truncated: panelKinds.truncated || autoPanelKinds.truncated,
  };
}

/** Counts the panel bank in one go. */
export function summarizePanelInventory(
  sections: readonly SectionWithRef[],
  keys: RunHistoryKeyInfoKeys = {}
) {
  const counting = countInventory(sections, keys);
  let step = counting.next();
  while (!step.done) {
    step = counting.next();
  }
  return step.value;
}

/**
 * The workspace labels of the Named workspaces event: personal or shared, with
 * a legacy prefix for a migrated legacy view. getNamedWorkspaceIdForView
 * throws on a malformed view name, which reports unknown.
 */
export function getInventoryWorkspaceType(
  view: Pick<LoadableView, 'name' | 'user'> | undefined
): NamedWorkspaceType {
  if (view?.user == null) {
    return 'unknown';
  }
  try {
    const {nwType} = getNamedWorkspaceIdForView(view.name, view.user);
    const type =
      nwType === 'shared workspace'
        ? 'shared'
        : nwType === 'personal workspace'
          ? 'personal'
          : 'unknown';
    return type !== 'unknown' && viewIsLegacyWorkspace(view.name)
      ? `legacy ${type}`
      : type;
  } catch {
    return 'unknown';
  }
}

/**
 * Sends `Workspace panel inventory` for the panel bank's sections: once per
 * workspace per browser every `SEND_INTERVAL_MS` (a day). The panel bank
 * mounts its config provider, which calls this, once auto-sync has loaded.
 */
export function useTrackPanelInventory(sections: readonly SectionWithRef[]) {
  const store = useReduxStore();
  const {viewRef} = useWorkspaceRefsContext();
  // Read through a ref: sections change on every panel edit, which must not
  // restart a count in progress.
  const sectionsRef = useRef(sections);
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);
  // The workspace this mount last sent or skipped, so switching workspace
  // without a remount (Copy to my workspace) still sends for the new one.
  const trackedKeyRef = useRef<string | undefined>(undefined);
  // Read at send time through a ref, so the mode resolving from 'pending'
  // doesn't restart a count in progress.
  const shouldAutoGeneratePanels = useShouldAutoGeneratePanels();
  const shouldAutoGeneratePanelsRef = useRef(shouldAutoGeneratePanels);
  useEffect(() => {
    shouldAutoGeneratePanelsRef.current = shouldAutoGeneratePanels;
  }, [shouldAutoGeneratePanels]);
  // Read through a ref, so a key-info refresh doesn't restart a count in
  // progress.
  const {historyKeyInfo} = useActiveHistoryKeyInfo();
  const historyKeysRef = useRef(historyKeyInfo.keys);
  useEffect(() => {
    historyKeysRef.current = historyKeyInfo.keys;
  }, [historyKeyInfo.keys]);

  useEffect(() => {
    const view = store.getState().views.views[viewRef.viewID];
    if (view == null) {
      return;
    }
    const workspaceKey = getInventoryWorkspaceKey(view);
    if (trackedKeyRef.current === workspaceKey) {
      return;
    }
    if (wasInventorySentRecently(workspaceKey)) {
      trackedKeyRef.current = workspaceKey;
      return;
    }

    const send = (inventory: ReturnType<typeof summarizePanelInventory>) => {
      trackedKeyRef.current = workspaceKey;
      // Read again: a first save during the idle wait gives the view its ID.
      const sentView = store.getState().views.views[viewRef.viewID] ?? view;
      trackWorkspacePanelInventory({
        schemaVersion: PANEL_INVENTORY_SCHEMA_VERSION,
        entityName: sentView.project?.entityName ?? sentView.entityName ?? '',
        projectName: sentView.project?.name ?? '',
        viewId: sentView.id,
        viewName: sentView.name,
        viewType: viewRef.type,
        workspaceType: getInventoryWorkspaceType(sentView),
        workspaceMode:
          shouldAutoGeneratePanelsRef.current === 'pending'
            ? 'pending'
            : shouldAutoGeneratePanelsRef.current
              ? 'auto'
              : 'manual',
        ...inventory,
      });
      recordInventorySent(workspaceKey);
    };
    return scheduleInventoryCount(
      // Snapshot when counting starts: auto-sync can add panels while idle
      // work is waiting to run.
      () => countInventory(sectionsRef.current, historyKeysRef.current),
      send
    );
  }, [store, viewRef]);
}
